import { useCallback, useContext, useEffect } from 'react';
import { QuizContext } from 'src/views/Quiz/Quiz.context';
import { QUIZ_EVENT_TYPES } from 'src/types/Quiz';
import styles from './quiz-how-it-works.module.scss';

const QuizHowItWorks = () => {
    const { currentQuestion, state, handleSubmitAnswer } =
        useContext(QuizContext);
    const { answerList, questionText, questionExplanation, questionImage } =
        currentQuestion || {};
    const [pageTransitioning] = state.pageTransitioning;
    const [, setContinueDisabled] = state.continueDisabled;

    const handleSubmit = useCallback(() => {
        if (currentQuestion.questionId) {
            setContinueDisabled(true);
            handleSubmitAnswer({
                questionId: currentQuestion.questionId,
                questionText: currentQuestion.questionText,
            });
        }
    }, [currentQuestion, handleSubmitAnswer]);

    // Continue click listener
    useEffect(() => {
        const handleContinueClick = () => {
            handleSubmit();
        };
        window.addEventListener(
            QUIZ_EVENT_TYPES.continueClick,
            handleContinueClick,
        );

        return () =>
            window.removeEventListener(
                QUIZ_EVENT_TYPES.continueClick,
                handleContinueClick,
            );
    }, [handleSubmit]);
    // Enter key press listener
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.key === 'Enter') handleSubmit();
        };
        window.addEventListener('keydown', handleKeyPress, false);

        return () =>
            window.removeEventListener('keydown', handleKeyPress, false);
    }, [handleSubmit]);
    // Continue state
    useEffect(() => {
        if (!pageTransitioning) {
            setContinueDisabled(false);
        }
    }, [pageTransitioning, setContinueDisabled]);

    return (
        <div className={styles['quiz-how-it-works--wrapper']}>
            {/* Content header */}
            <div className={styles['quiz-field-header']}>
                {/* Title */}
                <h3 className={styles['title']}>{questionText}</h3>
                {/* Subtitle */}
                {questionExplanation ? (
                    <h5 className="highlight color-grey-400">
                        {questionExplanation}
                    </h5>
                ) : null}
            </div>
            {/* Icon */}
            {questionImage ? (
                <img
                    className={styles['quiz-field-icon']}
                    src={questionImage}
                    alt={questionText}
                />
            ) : null}
            {/* List of answers */}
            <div className={styles['answers-list']}>
                {/* Answer */}
                {answerList.map((a, i) => (
                    <div key={i} className={styles['answer-item--wrapper']}>
                        <div className={styles['answer-item']}>
                            {a.image ? (
                                <img src={a.image} alt={a.answerText} />
                            ) : null}
                            <div className="flex flex-col space-y-2">
                                <span className="p1 highlight">
                                    {a.answerText}
                                </span>
                                <span className="p1 text-xs text-grey-400">
                                    {a.additionalField1}
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default QuizHowItWorks;
